// Currency formatting utility
// This utility fetches the organization's currency setting and formats amounts accordingly

import axios from 'axios';
import { API_URL } from './apiConfig';

// Cache for organization currency to avoid repeated API calls
let currencyCache = {
  currency: 'INR',
  timestamp: 0,
  cacheDuration: 1 * 60 * 1000 // 1 minute - reduced to ensure fresh currency data
};

/**
 * Get organization currency from cache or API
 * @returns {Promise<string>} Currency code (e.g., 'INR', 'USD')
 */
export const getOrganizationCurrency = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Return cached currency if still valid and not forcing refresh
  if (!forceRefresh && currencyCache.timestamp && (now - currencyCache.timestamp) < currencyCache.cacheDuration) {
    return currencyCache.currency;
  }

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return 'INR'; // Default currency
    }

    const response = await axios.get(`${API_URL}/organizations/my`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (response.data.success && response.data.data?.settings?.currency) {
      const currency = response.data.data.settings.currency;
      currencyCache = {
        currency,
        timestamp: now,
        cacheDuration: currencyCache.cacheDuration // Preserve cache duration
      };
      return currency;
    }
  } catch (err) {
    console.error('Error fetching organization currency:', err);
  }

  return currencyCache.currency || 'INR'; // Fallback to cached or default
};

/**
 * Get organization tax settings for invoices (default tax rate %, label, inclusive)
 * @returns {Promise<{ taxRate: number, taxLabel: string, taxInclusive: boolean }>}
 */
export const getOrganizationTaxSettings = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { taxRate: 0.18, taxLabel: 'GST', taxInclusive: false };

    const response = await axios.get(`${API_URL}/organizations/my`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.data.success && response.data.data?.settings) {
      const s = response.data.data.settings;
      const ratePct = Number(s.defaultTaxRate);
      const taxRate = (Number.isFinite(ratePct) ? ratePct / 100 : 0.18);
      const taxLabel = (s.taxLabel && String(s.taxLabel).trim()) || 'GST';
      // Treat true, 'true', 1 as inclusive so API/DB quirks don't drop the setting
      const taxInclusive = s.taxInclusive === true || s.taxInclusive === 'true' || s.taxInclusive === 1;
      return { taxRate, taxLabel, taxInclusive };
    }
  } catch (err) {
    console.error('Error fetching organization tax settings:', err);
  }
  return { taxRate: 0.18, taxLabel: 'GST', taxInclusive: false };
};

/**
 * Format currency amount based on organization settings
 * @param {number} amount - Amount to format
 * @param {string} currency - Optional currency code (if not provided, fetches from organization)
 * @returns {Promise<string>} Formatted currency string
 */
export const formatCurrency = async (amount, currency = null) => {
  if (amount === null || amount === undefined) {
    return '';
  }

  const currencyCode = currency || await getOrganizationCurrency();
  
  // Map currency codes to locale strings
  const localeMap = {
    'INR': 'en-IN',
    'USD': 'en-US',
    'EUR': 'en-EU',
    'GBP': 'en-GB',
    'ILS': 'he-IL' // Hebrew locale for Israeli Shekel
  };

  const locale = localeMap[currencyCode] || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0
    }).format(amount);
  } catch (err) {
    console.error('Error formatting currency:', err);
    // Fallback formatting
    return `${currencyCode} ${amount.toLocaleString()}`;
  }
};

/**
 * Synchronous version that uses cached currency
 * Use this for immediate formatting without async
 * @param {number} amount - Amount to format
 * @param {string} currency - Optional currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrencySync = (amount, currency = null) => {
  if (amount === null || amount === undefined) {
    return '';
  }

  const currencyCode = currency || currencyCache.currency || 'INR';
  
  const localeMap = {
    'INR': 'en-IN',
    'USD': 'en-US',
    'EUR': 'en-EU',
    'GBP': 'en-GB',
    'ILS': 'he-IL' // Hebrew locale for Israeli Shekel
  };

  const locale = localeMap[currencyCode] || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0
    }).format(amount);
  } catch (err) {
    console.error('Error formatting currency:', err);
    return `${currencyCode} ${amount.toLocaleString()}`;
  }
};

/**
 * Clear currency cache (call this when settings are updated)
 */
export const clearCurrencyCache = () => {
  currencyCache = {
    currency: 'INR',
    timestamp: 0
  };
};

/**
 * Set currency cache directly (useful when organization data is already loaded)
 * @param {string} currency - Currency code
 */
export const setCurrencyCache = (currency) => {
  currencyCache = {
    currency: currency || 'INR',
    timestamp: Date.now()
  };
};

/**
 * Get currency symbol for a given currency code
 * @param {string} currency - Currency code (defaults to cached currency)
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency = null) => {
  const currencyCode = currency || currencyCache.currency || 'INR';
  
  const symbolMap = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'ILS': '₪'
  };
  
  return symbolMap[currencyCode] || currencyCode;
};

